import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserContext } from "../../../contexts/UserContext";
import { updateTask } from "../../../utils/APIs";
import Dropdown from "../../../components/UI/Dropdown/Dropdown";
import { toast } from "react-toastify";
import { SecondaryButton, PrimaryButton } from "../../../components/UI";
import backwardArrow from "../../../assets/icons/backward-arrow.svg";
import Loader from "../../../components/UI/Loader/Loader";

const TaskConfig = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const { userData } = useUserContext();

    const [taskName, setTaskName] = useState(state?.saved_task_name || "");
    const [integrationId, setIntegrationId] = useState(state?.adobe_io_integration_id || "");
    const [integrations, setIntegrations] = useState([]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (userData?.adobeIoIntegrations) {
            setIntegrations(userData.adobeIoIntegrations);
        }
    }, [userData?.adobeIoIntegrations]);

    const validate = () => {
        const newErrors = {};
        if (!taskName.trim()) newErrors.taskName = "Task name is required.";
        if (!integrationId) newErrors.integrationId = "Adobe I/O integration is required.";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleUpdate = async () => {
        if (!validate()) return;

        setLoading(true);
        try {
            const result = await updateTask(userData.token, userData.selectedOrganization.org_token, {
                id: state?.saved_task_id,
                data: {
                    saved_task_name: taskName,
                    adobe_io_integration_id: integrationId,
                },
            });
            if (result.status) {
                toast.success(result.message);
            }
        } catch (err) {
            toast.error(err?.message || "Failed to update task.");
        } finally {
            setLoading(false);
        }
    };

    const handleViewData = () => {
        if (!integrationId) {
            setErrors({ integrationId: "Please select a valid Adobe I/O integration first." });
            return;
        }

        navigate(`/functions/one-off-tasks/${state?.funcId}`, {
            state: {
                tenant_name: integrations.find((i) => i._id === integrationId)?.tenant_name,
                campaign_id: state?.campaign_id,
            },
        });
    };

    const handleBack = () => navigate("/functions/saved-tasks");

    const adobeIoOptions =
        integrations?.map((integration) => ({
            value: integration._id,
            name: integration.integration_name,
        })) || [];

    return (
        <div className="flex flex-col w-full mx-4 md:mx-8 relative">
            {loading && <Loader />}
            <div className="my-4 w-full">
                <a
                    href="#"
                    className="font-medium text-primary hover:underline flex items-center"
                    onClick={(e) => {
                        e.preventDefault();
                        handleBack();
                    }}
                >
                    <img src={backwardArrow || "/placeholder.svg"} alt="" className="w-5 h-5 mr-2" /> Back to saved tasks
                </a>
            </div>

            <div className="w-full my-2 space-y-4">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <h2 className="text-xl font-medium">Task Configuration</h2>

                    <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                        <SecondaryButton 
                            size="base" 
                            handleClick={handleViewData}
                            className="w-full sm:w-auto"
                        >
                            View Data
                        </SecondaryButton>

                        <PrimaryButton 
                            size="base" 
                            handleClick={handleUpdate}
                            className="w-full sm:w-auto"
                        >
                            Update Task Configuration
                        </PrimaryButton>
                    </div>
                </div>

                <div>
                    <label className="block text-sm mb-1">Task Name</label>
                    <input
                        type="text"
                        value={taskName}
                        onChange={(e) => setTaskName(e.target.value)}
                        className={`w-full md:min-w-80 border px-3 py-2 rounded ${errors.taskName ? "border-red-500" : ""}`}
                    />
                    {errors.taskName && <p className="text-red-600 text-sm mt-1">{errors.taskName}</p>}
                </div>

                <div>
                    <label className="block text-sm mb-1">Adobe I/O Integration</label>
                    <Dropdown
                        width={window.innerWidth < 640 ? "100%" : 320}
                        initialValue={adobeIoOptions.find((opt) => opt.value === integrationId)}
                        items={adobeIoOptions}
                        callback={(val) => setIntegrationId(val.value)}
                    />
                    {errors.integrationId && <p className="text-red-600 text-sm mt-1">{errors.integrationId}</p>}
                </div>
            </div>
        </div>
    );
};

export default TaskConfig;
